import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject} from 'rxjs';
import {AuthenticationService} from '../authentication/authentication.service';
import {LocalStorageService} from '../local-storage/local-storage.service';

export const LANG_DISPLAY_NAMES = [
  {
    id: 'en-us',
    name: 'English',
    iconName: 'EN'
  },
  {
    id: 'pt-br',
    name: 'Português brasileiro',
    iconName: 'BR'
  },
  // {
  //   id: 'es-ES',
  //   name: 'Español',
  //   iconName: 'ES'
  // }
]

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  private readonly currentLangSubject = new BehaviorSubject<string>('en-us');

  constructor(
    private readonly translate: TranslateService,
    private readonly localStorageService: LocalStorageService,
    authenticationService: AuthenticationService,
  ) {
    // Add supported languages
    this.translate.addLangs(LANG_DISPLAY_NAMES.map(lang => lang.id));

    // Set default language
    this.translate.setDefaultLang('en-us');

    if (authenticationService.isAuthenticated && authenticationService.userData) {
      if (this.isLanguageAvailable(authenticationService.userData.locale)) {
        this.setLanguage(authenticationService.userData.locale);
        return;
      }
    }

    const selectedLanguage = this.localStorageService.get<string>('WCO-Language');
    if (selectedLanguage && this.isLanguageAvailable(selectedLanguage)) {
      this.setLanguage(selectedLanguage);
      return;
    }

    // Try to get browser language
    const browserLang = this.translate.getBrowserLang();

    // Use browser language if it's available, otherwise use default
    const initialLang = browserLang && this.isLanguageAvailable(browserLang)
      ? browserLang
      : 'en-us';

    this.setLanguage(initialLang);
  }

  /**
   * Checks if a specified language is available in the list of supported languages.
   *
   * @param {string} lang - The language code to check for availability.
   * @return {boolean} True if the specified language is available; otherwise, false.
   */
  public isLanguageAvailable(lang: string): boolean {
    return this.translate.getLangs().includes(lang);
  }

  /**
   * Sets the application's language and updates necessary services and storage with the specified language.
   *
   * @param {string} lang - The language code to set as the current language.
   * @return {void}
   */
  public setLanguage(lang: string): void {
    this.translate.use(lang);
    this.currentLangSubject.next(lang);
    this.localStorageService.set('WCO-Language', lang, 365 * 24 * 60 * 60);
  }

  /**
   * Retrieves the current language value.
   *
   * @return {string} The current language as a string.
   */
  public getCurrentLang(): string {
    return this.currentLangSubject.value;
  }

  public getCurrentLangObj() {
    return LANG_DISPLAY_NAMES.find(lang => lang.id === this.getCurrentLang()) ?? LANG_DISPLAY_NAMES[0];
  }

  /**
   * Retrieves a list of available languages supported by the translation service.
   *
   * @return {string[]} An array of language codes representing the available languages.
   */
  public getAvailableLanguages() {
    return LANG_DISPLAY_NAMES;
  }
}
